import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Ship, Plus, Save, Trash2, X, MapPin, Image as ImageIcon, ExternalLink } from "lucide-react";
import { extractApiError } from "../../lib/apiError";

/** Admin CRUD tab for Venice Hotels + landing-page settings.
 * Requires the `venice_hotels` permission (super_admin auto-passes). */
export default function VeniceHotelsTab() {
  const [hotels, setHotels] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingHotel, setEditingHotel] = useState(null);
  const [savingPage, setSavingPage] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [h, p] = await Promise.all([
        axios.get(`${API}/admin/venice-hotels`),
        axios.get(`${API}/venice-hotels-page`),
      ]);
      setHotels(h.data || []);
      setPage(p.data || {});
    } catch (err) {
      toast.error(extractApiError(err, "Failed to load hotels"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const savePage = async () => {
    setSavingPage(true);
    try {
      await axios.put(`${API}/admin/venice-hotels-page`, page);
      toast.success("Landing page updated");
    } catch (err) {
      toast.error(extractApiError(err, "Failed to save landing page"));
    } finally {
      setSavingPage(false);
    }
  };

  const deleteHotel = async (hotel) => {
    if (!window.confirm(`Delete "${hotel.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/venice-hotels/${hotel.hotel_id}`);
      toast.success("Hotel removed");
      fetchAll();
    } catch (err) {
      toast.error(extractApiError(err, "Delete failed"));
    }
  };

  return (
    <div className="space-y-10" data-testid="venice-hotels-tab">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Ship className="w-5 h-5 text-[#C5A059]" /> Venice Hotels
          </h2>
          <p className="text-white/40 text-xs mt-1">
            CRUD for the &ldquo;Hotel in Venice&rdquo; sub-option — airport ↔ hotel flat price + surcharge for other routes.
          </p>
        </div>
        <Button className="btn-gold" onClick={() => setEditingHotel({ __new__: true, name: "", slug: "", airport_price: 160, surcharge: 25, disabled: false })} data-testid="add-hotel-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add hotel
        </Button>
      </div>

      {/* Landing-page settings */}
      {page && (
        <section className="bg-[#151515] border border-white/5 rounded-sm p-5" data-testid="hotels-page-settings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Landing page (/hotels-in-venice)</h3>
            <a href="/hotels-in-venice" target="_blank" rel="noreferrer" className="text-[#C5A059] text-xs inline-flex items-center gap-1 hover:underline">
              Open live <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Hero title</label>
              <Input className="input-dark" value={page.hero_title || ""} onChange={e => setPage(p => ({ ...p, hero_title: e.target.value }))} data-testid="page-hero-title" />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Hero subtitle</label>
              <Input className="input-dark" value={page.hero_subtitle || ""} onChange={e => setPage(p => ({ ...p, hero_subtitle: e.target.value }))} data-testid="page-hero-subtitle" />
            </div>
            <div className="md:col-span-2">
              <label className="text-white/50 text-xs uppercase mb-1 block">Hero image URL (https:// or /path)</label>
              <Input className="input-dark font-mono" value={page.hero_image_url || ""} onChange={e => setPage(p => ({ ...p, hero_image_url: e.target.value }))} placeholder="Leave empty for default Venice stock image" />
            </div>
            <div className="md:col-span-2">
              <label className="text-white/50 text-xs uppercase mb-1 block">Intro (HTML / Markdown — sanitized on render)</label>
              <Textarea className="input-dark min-h-[120px] font-mono text-xs" value={page.intro_html || ""} onChange={e => setPage(p => ({ ...p, intro_html: e.target.value }))} placeholder="Long editable description shown between hero and hotel grid" />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">SEO Meta title</label>
              <Input className="input-dark" value={page.meta_title || ""} onChange={e => setPage(p => ({ ...p, meta_title: e.target.value }))} maxLength={80} />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">SEO Meta description</label>
              <Input className="input-dark" value={page.meta_description || ""} onChange={e => setPage(p => ({ ...p, meta_description: e.target.value }))} maxLength={200} />
            </div>
            <div className="md:col-span-2">
              <label className="text-white/50 text-xs uppercase mb-1 block">Keywords (comma-separated)</label>
              <Input className="input-dark" value={page.keywords || ""} onChange={e => setPage(p => ({ ...p, keywords: e.target.value }))} />
            </div>
          </div>
          <Button className="btn-gold mt-4" onClick={savePage} disabled={savingPage} data-testid="save-hotels-page-btn">
            <Save className="w-4 h-4 mr-1.5" /> {savingPage ? "Saving..." : "Save landing page"}
          </Button>
        </section>
      )}

      {/* Hotels list */}
      <section>
        <h3 className="text-white font-medium mb-4">Hotels ({hotels.length})</h3>
        {loading ? (
          <p className="text-white/50">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hotels.map(h => (
              <div key={h.hotel_id} className={`border rounded-sm p-4 flex items-start gap-3 ${h.disabled ? "border-white/5 bg-[#0F0F0F] opacity-60" : "border-white/10 bg-[#151515]"}`} data-testid={`hotel-row-${h.slug}`}>
                <div className="w-16 h-16 rounded-sm border border-white/5 overflow-hidden flex-shrink-0 bg-[#0F0F0F] flex items-center justify-center">
                  {h.hero_image_url ? <img src={h.hero_image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-white/20" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-medium truncate">{h.name}</h4>
                    {h.disabled && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300">disabled</span>}
                  </div>
                  <p className="text-white/40 text-[11px] font-mono truncate">{h.slug}</p>
                  <div className="flex gap-4 text-xs mt-1">
                    <span className="text-[#C5A059]">Airport €{(h.airport_price || 0).toFixed(0)}</span>
                    <span className="text-white/60">+ €{(h.surcharge || 0).toFixed(0)} elsewhere</span>
                  </div>
                  {h.address && <p className="text-white/50 text-xs mt-1 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 text-[#C5A059] flex-shrink-0" />{h.address}</p>}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => setEditingHotel(h)} className="text-[#C5A059] hover:bg-[#C5A059]/10 rounded p-1.5" title="Edit" data-testid={`edit-hotel-${h.slug}`}>
                    <Save className="w-4 h-4" />
                  </button>
                  <a href={`/hotels-in-venice/${h.slug}`} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white p-1.5" title="Open live page"><ExternalLink className="w-4 h-4" /></a>
                  <button onClick={() => deleteHotel(h)} className="text-rose-400 hover:bg-rose-400/10 rounded p-1.5" title="Delete" data-testid={`delete-hotel-${h.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {editingHotel && (
        <HotelEditModal
          hotel={editingHotel}
          onClose={() => setEditingHotel(null)}
          onSaved={() => { setEditingHotel(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function HotelEditModal({ hotel, onClose, onSaved }) {
  const isNew = !!hotel.__new__;
  const [form, setForm] = useState({ ...hotel });
  const [saving, setSaving] = useState(false);

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        airport_price: parseFloat(form.airport_price) || 0,
        surcharge: parseFloat(form.surcharge) || 0,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        display_order: parseInt(form.display_order) || 0,
      };
      delete payload.__new__;
      delete payload.hotel_id;
      delete payload.created_at;
      delete payload.updated_at;
      if (isNew) {
        await axios.post(`${API}/admin/venice-hotels`, payload);
        toast.success("Hotel created");
      } else {
        await axios.put(`${API}/admin/venice-hotels/${hotel.hotel_id}`, payload);
        toast.success("Hotel updated");
      }
      onSaved();
    } catch (err) {
      toast.error(extractApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="hotel-edit-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Ship className="w-5 h-5 text-[#C5A059]" /> {isNew ? "New hotel" : form.name}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="hotel-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Name *</label>
              <Input className="input-dark" value={form.name || ""} onChange={e => u("name", e.target.value)} data-testid="hotel-name" />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Slug * (lowercase, kebab-case)</label>
              <Input className="input-dark font-mono" value={form.slug || ""} onChange={e => u("slug", e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, "-"))} disabled={!isNew} data-testid="hotel-slug" />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Airport price (€) — Airport ↔ Hotel + Near Venice ↔ Hotel</label>
              <Input type="number" step="1" className="input-dark" value={form.airport_price ?? 0} onChange={e => u("airport_price", e.target.value)} data-testid="hotel-airport-price" />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Surcharge (€) — added to other routes</label>
              <Input type="number" step="1" className="input-dark" value={form.surcharge ?? 25} onChange={e => u("surcharge", e.target.value)} data-testid="hotel-surcharge" />
            </div>
            <div className="md:col-span-2">
              <label className="text-white/50 text-xs uppercase mb-1 block">Address</label>
              <Input className="input-dark" value={form.address || ""} onChange={e => u("address", e.target.value)} />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Latitude</label>
              <Input type="number" step="0.0001" className="input-dark font-mono" value={form.latitude ?? ""} onChange={e => u("latitude", e.target.value)} placeholder="45.4321" />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Longitude</label>
              <Input type="number" step="0.0001" className="input-dark font-mono" value={form.longitude ?? ""} onChange={e => u("longitude", e.target.value)} placeholder="12.3308" />
            </div>
            <div className="md:col-span-2">
              <label className="text-white/50 text-xs uppercase mb-1 block">Hero image URL (https:// or /path)</label>
              <Input className="input-dark font-mono" value={form.hero_image_url || ""} onChange={e => u("hero_image_url", e.target.value)} placeholder="/hotel_images/hotel-slug.png or full https URL" />
              {form.hero_image_url && (
                <img src={form.hero_image_url} alt="" className="mt-2 rounded-sm border border-white/5 max-h-40 object-cover" />
              )}
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">Display order</label>
              <Input type="number" step="1" className="input-dark" value={form.display_order ?? 0} onChange={e => u("display_order", e.target.value)} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!form.disabled} onCheckedChange={(v) => u("disabled", !v)} data-testid="hotel-active-toggle" />
              <span className="text-white/70 text-sm">{form.disabled ? "Disabled (hidden from customers)" : "Active (shown on site)"}</span>
            </div>
          </div>

          <div>
            <label className="text-white/50 text-xs uppercase mb-1 block">Short description (1 line)</label>
            <Input className="input-dark" value={form.short_description || ""} onChange={e => u("short_description", e.target.value)} maxLength={200} />
          </div>
          <div>
            <label className="text-white/50 text-xs uppercase mb-1 block">Long description (HTML / Markdown — shown on the hotel page)</label>
            <Textarea className="input-dark min-h-[160px] font-mono text-xs" value={form.long_description || ""} onChange={e => u("long_description", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/5">
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">SEO Meta title</label>
              <Input className="input-dark" value={form.meta_title || ""} onChange={e => u("meta_title", e.target.value)} maxLength={80} />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase mb-1 block">SEO Meta description</label>
              <Input className="input-dark" value={form.meta_description || ""} onChange={e => u("meta_description", e.target.value)} maxLength={200} />
            </div>
            <div className="md:col-span-2">
              <label className="text-white/50 text-xs uppercase mb-1 block">Keywords</label>
              <Input className="input-dark" value={form.keywords || ""} onChange={e => u("keywords", e.target.value)} />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-white/5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-white/70">Cancel</Button>
          <Button onClick={save} disabled={saving} className="btn-gold" data-testid="hotel-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save hotel"}
          </Button>
        </div>
      </div>
    </div>
  );
}
